import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Award, Share2, Download, Flag, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";

const PODIUM_ANIMS = {
  first: { initial: { opacity: 0, y: 50 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.1 } },
  second: { initial: { opacity: 0, y: 50 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.2 } },
  third: { initial: { opacity: 0, y: 50 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.3 } },
};

export default function PublicResultsPage() {
  const { competitionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResults = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/results/${competitionId}`);
      setData(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching results:", error);
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    fetchResults();
    const interval = setInterval(fetchResults, 10000); // Poll every 10 seconds
    return () => clearInterval(interval);
  }, [fetchResults]);

  const handleShare = async () => {
    try {
      await navigator.share({
        title: `${data?.festival_name} - ${data?.category} Results`,
        url: window.location.href
      });
    } catch (error) {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${API}/export/results/${competitionId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { festival_name, festival_logo, category, status, top_leaders, top_followers, round_name, is_final } = data || {};

  const TopThree = ({ dancers, title, color }) => (
    <div className="mb-12">
      <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-6 flex items-center gap-3">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
        Top {dancers?.length || 0} {title}
      </h3>
      
      {/* Podium for top 3 */}
      <div className="flex items-end justify-center gap-4 mb-8">
        {/* 2nd Place */}
        {dancers[1] && (
          <motion.div
            {...PODIUM_ANIMS.second}
            className="w-1/3 max-w-[150px]"
          >
            <div className="text-center mb-2">
              <p className="text-white font-medium text-sm truncate">{dancers[1].name}</p>
              <p className="text-[#A1A1AA] text-xs">{dancers[1].country}</p>
              <p className="text-[#FFD700] font-bold">{(dancers[1].combined_score ?? dancers[1].average_score ?? 0).toFixed(2)}</p>
            </div>
            <div className="h-24 bg-gradient-to-t from-[#C0C0C0]/20 to-[#C0C0C0]/5 rounded-t-lg flex items-center justify-center border-t-4 border-[#C0C0C0]">
              <span className="font-['Playfair_Display'] text-4xl font-bold text-[#C0C0C0]">2</span>
            </div>
          </motion.div>
        )}
        
        {/* 1st Place */}
        {dancers[0] && (
          <motion.div
            {...PODIUM_ANIMS.first}
            className="w-1/3 max-w-[180px]"
          >
            <div className="text-center mb-2">
              <Trophy className="w-8 h-8 text-[#FFD700] mx-auto mb-1" />
              <p className="text-white font-bold truncate">{dancers[0].name}</p>
              <p className="text-[#A1A1AA] text-xs">{dancers[0].country}</p>
              <p className="text-[#FFD700] text-xl font-bold">{(dancers[0].combined_score ?? dancers[0].average_score ?? 0).toFixed(2)}</p>
            </div>
            <div className="h-32 bg-gradient-to-t from-[#FFD700]/20 to-[#FFD700]/5 rounded-t-lg flex items-center justify-center border-t-4 border-[#FFD700] gold-glow">
              <span className="font-['Playfair_Display'] text-5xl font-bold text-[#FFD700]">1</span>
            </div>
          </motion.div>
        )}
        
        {/* 3rd Place */}
        {dancers[2] && (
          <motion.div
            {...PODIUM_ANIMS.third}
            className="w-1/3 max-w-[150px]"
          >
            <div className="text-center mb-2">
              <p className="text-white font-medium text-sm truncate">{dancers[2].name}</p>
              <p className="text-[#A1A1AA] text-xs">{dancers[2].country}</p>
              <p className="text-[#FFD700] font-bold">{(dancers[2].combined_score ?? dancers[2].average_score ?? 0).toFixed(2)}</p>
            </div>
            <div className="h-16 bg-gradient-to-t from-[#CD7F32]/20 to-[#CD7F32]/5 rounded-t-lg flex items-center justify-center border-t-4 border-[#CD7F32]">
              <span className="font-['Playfair_Display'] text-3xl font-bold text-[#CD7F32]">3</span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Full rankings list */}
      <div className="space-y-2">
        {dancers.slice(3).map((dancer, index) => (
          <motion.div
            key={dancer.dancer_id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + index * 0.05 }}
            className="flex items-center p-4 bg-[#18181b]/50 rounded-lg"
            data-testid={`result-${dancer.dancer_id}`}
          >
            <span className="font-['Playfair_Display'] text-xl font-bold text-[#52525B] w-10">
              {index + 4}
            </span>
            <div className="flex-1 ml-4">
              <p className="text-white font-medium">{dancer.name}</p>
              <p className="text-[#A1A1AA] text-sm flex items-center gap-1">
                <Flag className="w-3 h-3" /> {dancer.country}
              </p>
            </div>
            <span className="text-[#FFD700] font-bold">{(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(2)}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );

  return (
    <div 
      className="min-h-screen bg-[#09090b] bg-cover bg-center relative"
      style={{ 
        backgroundImage: `url('https://images.unsplash.com/photo-1741356474365-5f0041f89eaa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBnb2xkJTIwdHJvcGh5JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcyODA4MDQ4fDA&ixlib=rb-4.1.0&q=85')` 
      }}
    >
      <div className="absolute inset-0 bg-[#09090b]/95" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Festival title with logos on sides */}
          <div className="flex items-center justify-center gap-8 mb-6">
            <Link to="/" className="flex flex-col items-center flex-shrink-0">
              <img src="/jj-logo.png" alt="J&J" className="w-40 h-40" />
              <span className="font-['Playfair_Display'] text-sm font-bold text-[#A1A1AA] mt-1">jackandjillscore.com</span>
            </Link>
            
            <div className="text-center">
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white mb-3">
                {festival_name}
              </h1>
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="text-[#FFD700] text-xl font-medium">{category}</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium uppercase ${
                  status === 'completed' ? 'bg-green-500/20 text-green-500' :
                  status === 'active' ? 'bg-[#FF007F]/20 text-[#FF007F]' :
                  'bg-[#A1A1AA]/20 text-[#A1A1AA]'
                }`}>
                  {status === 'active' && '● '}{status}
                </span>
                {round_name && (
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-[#A855F7]/20 text-[#A855F7]">
                    {is_final ? 'Final Results' : round_name}
                  </span>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <Button onClick={handleShare} variant="outline" className="btn-outline" data-testid="share-btn">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button onClick={handleDownloadPDF} className="btn-gold" data-testid="download-results-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            {festival_logo ? (
              <img src={festival_logo} alt="Event" className="w-40 h-40 object-contain rounded-lg flex-shrink-0" />
            ) : <div className="w-40 flex-shrink-0"></div>}
          </div>
        </motion.div>

        {/* Results */}
        <div className="grid md:grid-cols-2 gap-12">
          <TopThree dancers={top_leaders || []} title="Leaders" color="#FFD700" />
          <TopThree dancers={top_followers || []} title="Followers" color="#FF007F" />
        </div>

        {/* Live Display Link */}
        <div className="text-center mt-12 pt-8 border-t border-white/10">
          <Link to={`/live/${competitionId}`} target="_blank">
            <Button variant="outline" className="btn-outline">
              <ExternalLink className="w-4 h-4 mr-2" />
              View Live Display
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
